import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, CheckCircle, Building2, Wrench, Users, Phone, Mail, MapPin, ChevronRight, Play, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { companyInfo, aboutData, services } from '../mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch projects on component mount
  const fetchProjects = useCallback(async () => {
    try {
      setIsLoadingProjects(true);
      const response = await fetch(`${BACKEND_URL}/api/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      } else {
        console.error('Failed to fetch projects');
        toast({
          title: "Hata",
          description: "Projeler yüklenirken bir hata oluştu.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: "Hata",
        description: "Projeler yüklenirken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingProjects(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Projeleri durumlarına göre grupla
  const ongoingProjects = projects.filter(p => p.status === 'devam_ediyor');
  const completedProjects = projects.filter(p => p.status === 'tamamlandi');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Mesajınız alındı!",
          description: "En kısa sürede size dönüş yapacağız.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Hata",
        description: "Mesajınız gönderilemedi. Lütfen tekrar deneyin.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1487491424367-7571f9afbb30?w=1920')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {companyInfo.name}
            </h1>
            <p className="text-xl md:text-2xl text-amber-400 mb-4 font-semibold">
              {companyInfo.slogan}
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              Kaliteli mühendislik çözümleri ve güvenilir hizmet anlayışımızla projelerinizi hayata geçiriyoruz.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              >
                İletişime Geçin
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg transition-all duration-300"
              >
                Hizmetlerimiz
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {aboutData.title}
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {aboutData.description.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-amber-500 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900">Kaliteli Hizmet</h4>
                    <p className="text-sm text-gray-600">Profesyonel ekip</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-amber-500 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900">Deneyim</h4>
                    <p className="text-sm text-gray-600">Uzman kadro</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src={aboutData.image}
                alt="Hakkımızda"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Hizmetlerimiz</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Betonarme, çelik yapı, endüstriyel tesis ve villa projelerinde profesyonel hizmet sunuyoruz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 overflow-hidden"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
                    {service.title}
                  </h3>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRight size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Projelerimiz</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Devam eden ve tamamladığımız başarılı projelerden örnekler
            </p>
          </div>

          {isLoadingProjects ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
              <p className="mt-4 text-gray-600">Projeler yükleniyor...</p>
            </div>
          ) : (
            <div className="space-y-16">
              {/* Devam Eden Projeler */}
              {ongoingProjects.length > 0 && (
                <div>
                  <div className="mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">Devam Eden Projeler</h3>
                    <div className="h-1 w-24 bg-amber-500"></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {ongoingProjects.map((project) => (
                      <Card
                        key={project.id}
                        className="group overflow-hidden border-0 hover:shadow-xl transition-all duration-300 cursor-pointer"
                        onClick={() => setSelectedProject(project)}
                      >
                        <div className="relative h-80 overflow-hidden">
                          <img
                            src={project.thumbnail}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <div className="bg-amber-500 rounded-full p-4">
                              <Play size={32} className="text-black" fill="black" />
                            </div>
                          </div>
                          <div className="absolute top-4 left-4 bg-amber-500 text-black text-xs font-semibold px-3 py-1 rounded">
                            Devam Ediyor
                          </div>
                          <div className="absolute top-4 right-4 bg-amber-500 text-black text-xs font-semibold px-3 py-1 rounded">
                            {project.year}
                          </div>
                        </div>
                        <CardContent className="p-6">
                          <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">
                            {project.category}
                          </span>
                          <h3 className="text-xl font-bold text-gray-900 mt-2 mb-1">
                            {project.title} <span className="text-amber-600">({project.area})</span>
                          </h3>
                          {project.description && (
                            <p className="text-sm text-gray-600">{project.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Tamamlanmış Projeler */}
              {completedProjects.length > 0 && (
                <div>
                  <div className="mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">Tamamlanmış Projeler</h3>
                    <div className="h-1 w-24 bg-green-600"></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {completedProjects.map((project) => (
                      <Card
                        key={project.id}
                        className="group overflow-hidden border-0 hover:shadow-xl transition-all duration-300 cursor-pointer"
                        onClick={() => setSelectedProject(project)}
                      >
                        <div className="relative h-80 overflow-hidden">
                          <img
                            src={project.thumbnail}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <div className="bg-green-600 rounded-full p-4">
                              <CheckCircle size={32} className="text-white" />
                            </div>
                          </div>
                          <div className="absolute top-4 left-4 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded">
                            Tamamlandı
                          </div>
                          <div className="absolute top-4 right-4 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded">
                            {project.year}
                          </div>
                        </div>
                        <CardContent className="p-6">
                          <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                            {project.category}
                          </span>
                          <h3 className="text-xl font-bold text-gray-900 mt-2 mb-1">
                            {project.title} <span className="text-green-600">({project.area})</span>
                          </h3>
                          {project.description && (
                            <p className="text-sm text-gray-600">{project.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Hiç proje yoksa */}
              {ongoingProjects.length === 0 && completedProjects.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600">Henüz proje eklenmemiş.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Project Image Modal */}
      <Dialog open={selectedProject !== null} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedProject?.title} <span className="text-amber-600">({selectedProject?.area})</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="w-full">
              <img
                src={selectedProject?.thumbnail}
                alt={selectedProject?.title}
                className="w-full h-auto rounded-lg"
              />
            </div>
            {selectedProject?.description && (
              <div>
                <p className="text-gray-600 mb-3">{selectedProject?.description}</p>
                <div className="flex gap-4 text-sm">
                  <span className="font-semibold text-amber-600">{selectedProject?.category}</span>
                  <span className="text-gray-500">{selectedProject?.year}</span>
                  <span className="text-gray-500">{selectedProject?.area}</span>
                </div>
              </div>
            )}
            {!selectedProject?.description && (
              <div className="flex gap-4 text-sm">
                <span className="font-semibold text-amber-600">{selectedProject?.category}</span>
                <span className="text-gray-500">{selectedProject?.year}</span>
                <span className="text-gray-500">{selectedProject?.area}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">İletişim</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Projeleriniz için bizimle iletişime geçin
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">İletişim Bilgileri</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <MapPin className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Adres</h4>
                    <p className="text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <Phone className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Telefon</h4>
                    <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-amber-600">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <Mail className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">E-posta</h4>
                    <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-amber-600">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                {companyInfo.social.instagram && (
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-100 p-3 rounded-lg">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="text-amber-600"
                      >
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Instagram</h4>
                      <a 
                        href={companyInfo.social.instagram} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-amber-600"
                      >
                        kolhanmuhendislik
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Çalışma Saatleri</h4>
                <div className="space-y-2 text-gray-600">
                  <p>Pazartesi - Cuma: 09:00 - 18:00</p>
                  <p>Cumartesi: 09:00 - 14:00</p>
                  <p>Pazar: Kapalı</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle>Bize Ulaşın</CardTitle>
                  <CardDescription>Formu doldurun, size dönüş yapalım</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        name="name"
                        placeholder="Adınız Soyadınız"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Input
                        name="email"
                        type="email"
                        placeholder="E-posta Adresiniz"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Input
                        name="phone"
                        type="tel"
                        placeholder="Telefon Numaranız"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Textarea
                        name="message"
                        placeholder="Mesajınız"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold py-6 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;